import { memo, useCallback, useEffect, useState } from "react";
import {
	Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { sendFeedback } from "../api/index.js";
import { useSnackbar } from "../utils/index.js";
import {
	FEEDBACK_MAX, FEEDBACK_MIN, FEEDBACK_TYPES,
	buildFeedbackPayload, feedbackErrorKey, onContactRequest, pageNameForReport, validateFeedbackMessage,
} from "../utils/contact-feedback.js";

const ERROR_TEXT = {
	validation: "legal.contact.errorValidation",
	rateLimit: "legal.contact.errorRateLimit",
	generic: "legal.contact.errorGeneric",
};
const MESSAGE_ERROR_TEXT = {
	tooShort: "legal.contact.errorTooShort",
	tooLong: "legal.contact.errorTooLong",
};

/**
 * The contact / bug report dialog for signed-in users. Mount it once (index.js); open it from anywhere with
 * openContactDialog() from utils/contact-feedback.js.
 */
const ContactDialog = () => {
	const { t } = useTranslation();
	const snackbar = useSnackbar();
	const [open, setOpen] = useState(false);
	const [type, setType] = useState("bug");
	const [message, setMessage] = useState("");
	const [page, setPage] = useState("");
	const [touched, setTouched] = useState(false);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => onContactRequest(() => {
		setType("bug");
		setMessage("");
		setTouched(false);
		setError(null);
		setPage(pageNameForReport(window.location.href));
		setOpen(true);
	}), []);

	const close = useCallback(() => {
		if (!sending) setOpen(false);
	}, [sending]);

	const messageError = validateFeedbackMessage(message);
	const showMessageError = touched && messageError !== null;

	const submit = async (event) => {
		event.preventDefault();
		setTouched(true);
		if (messageError || sending) return;
		setSending(true);
		setError(null);
		let result;
		let failure;
		try {
			result = await sendFeedback(buildFeedbackPayload({ type, message, page, userAgent: navigator.userAgent }));
		} catch (error_) {
			failure = error_;
		}

		setSending(false);
		const errorKey = feedbackErrorKey(result, failure);
		if (errorKey) {
			// Keep the dialog open so the text is not lost
			setError(errorKey);
			return;
		}

		setOpen(false);
		snackbar.success(t("legal.contact.success"));
	};

	return (
		<Dialog fullWidth maxWidth="sm" open={open} PaperProps={{ component: "form", onSubmit: submit, noValidate: true }} onClose={close}>
			<DialogTitle>{t("legal.contact.title")}</DialogTitle>
			<DialogContent>
				{error && <Alert role="alert" severity="error" sx={{ mb: 2 }}>{t(ERROR_TEXT[error])}</Alert>}
				<TextField
					fullWidth
					select
					label={t("legal.contact.typeLabel")}
					margin="normal"
					size="small"
					value={type}
					onChange={(event) => setType(event.target.value)}
				>
					{FEEDBACK_TYPES.map((value) => <MenuItem key={value} value={value}>{t(`legal.contact.types.${value}`)}</MenuItem>)}
				</TextField>
				<TextField
					fullWidth
					multiline
					required
					error={showMessageError}
					helperText={showMessageError
						? t(MESSAGE_ERROR_TEXT[messageError], { min: FEEDBACK_MIN, max: FEEDBACK_MAX })
						: t("legal.contact.messageHelp", { count: message.trim().length, min: FEEDBACK_MIN, max: FEEDBACK_MAX })}
					label={t("legal.contact.messageLabel")}
					margin="normal"
					minRows={5}
					value={message}
					onBlur={() => setTouched(true)}
					onChange={(event) => setMessage(event.target.value)}
				/>
				<TextField
					fullWidth
					InputProps={{ readOnly: true }}
					helperText={t("legal.contact.pageHelp")}
					label={t("legal.contact.pageLabel")}
					margin="normal"
					size="small"
					value={page}
				/>
			</DialogContent>
			<DialogActions>
				<Button disabled={sending} type="button" onClick={close}>{t("legal.contact.cancel")}</Button>
				<Button disabled={sending} type="submit" variant="contained">{sending ? t("legal.contact.sending") : t("legal.contact.send")}</Button>
			</DialogActions>
		</Dialog>
	);
};

export default memo(ContactDialog);
